import { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  TextField,
  Select as MuiSelect,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material'
import { usePOTracking } from '@/hooks/usePOTracking'
import { OverviewChart } from '@/components/PoTracking/OverviewChart'
import { POTable } from '@/components/PoTracking/POTable'
import { POImportDialog } from '@/components/PoTracking/POImportDialog'
import type { ForecastConfidence, ForecastObjective, PurchaseOrderWithUsage } from '@/types/po'
import { useT } from '@/i18n/useT'
import { LEGAL_COUNSELS } from '@/utils/legalCounsel'
import { INVOICE_ENTITIES } from '@/utils/invoiceEntity'
import {
  Button,
  Card,
  CardTitle,
  Icon,
  Notice,
  Page,
  PageHeader,
  Pill,
  Select,
} from '@/components/ui'

const ADD_YEAR_VALUE = -1
const lineTypeOptions = [
  { value: 'linear', label: 'Linear' },
  { value: 'monotone', label: 'Monotone' },
  { value: 'step', label: 'Step' },
  { value: 'stepAfter', label: 'Step After' },
] as const

type LineTypeOption = typeof lineTypeOptions[number]['value']

/** What the create/edit dialog collects. Mirrors PurchaseOrderInput's writable half. */
interface PoFormData {
  poNumber: string
  pivotPoNumber: string | null
  supplierName: string | null
  description: string | null
  responsiblePerson: string | null
  legalCounsel: string | null
  entity: string | null
  valueTotalEur: number
  year: number
}
const FORECAST_OBJECTIVE: ForecastObjective = 'balanced'

function confidenceTone(confidence: ForecastConfidence): { fg: string; bg: string } {
  if (confidence === 'high') return { fg: 'var(--ok)', bg: 'var(--okSoft)' }
  if (confidence === 'medium') return { fg: 'var(--warn)', bg: 'var(--warnSoft)' }
  return { fg: 'var(--err)', bg: 'var(--errSoft)' }
}

export default function PoTrackingPage() {
  const { t } = useT()
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())
  const [filteredPOId, setFilteredPOId] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingPO, setEditingPO] = useState<PurchaseOrderWithUsage | null>(null)
  const [customYears, setCustomYears] = useState<number[]>([])
  const [lineType, setLineType] = useState<LineTypeOption>('stepAfter')
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [addYear, setAddYear] = useState<{ open: boolean; onAdded?: (y: number) => void; value: string; error: string }>({
    open: false,
    value: '',
    error: '',
  })

  const [importOpen, setImportOpen] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const [importNotice, setImportNotice] = useState<string | null>(null)

  const {
    useYears,
    usePOsWithUsage,
    useForecast,
    createPO,
    updatePO,
    deletePO,
    importPOs,
    applyAssignments,
  } = usePOTracking()

  const { data: years = [] } = useYears()
  const { data: pos = [], isLoading: posLoading, refetch: refetchPOs } = usePOsWithUsage(selectedYear)
  const { data: forecast, isLoading: forecastLoading } = useForecast(
    selectedYear,
    filteredPOId ?? undefined,
    FORECAST_OBJECTIVE
  )

  const handleCreatePO = () => {
    setEditingPO(null)
    setDialogOpen(true)
  }

  const handleEditPO = (po: PurchaseOrderWithUsage) => {
    setEditingPO(po)
    setDialogOpen(true)
  }

  const handleDeletePO = (id: string) => setDeleteTargetId(id)

  const confirmDeletePO = async () => {
    if (!deleteTargetId) return
    await deletePO.mutateAsync(deleteTargetId)
    setDeleteTargetId(null)
    refetchPOs()
  }

  const handleSavePO = async (data: PoFormData) => {
    if (editingPO) {
      await updatePO.mutateAsync({ id: editingPO.id, data })
    } else {
      await createPO.mutateAsync(data)
    }
    setDialogOpen(false)
    refetchPOs()
  }

  const handleApplyAssignments = async () => {
    setImportError(null)
    try {
      const { updated } = await applyAssignments.mutateAsync()
      setImportNotice(
        t('{n} invoice(s) completed with entity, approver and legal counsel.').replace(
          '{n}',
          String(updated),
        ),
      )
    } catch (e) {
      setImportNotice(e instanceof Error ? e.message : t('Request failed'))
    }
  }

  const handleFilterByPO = (poId: string | null) => {
    setFilteredPOId(poId)
  }

  const handleImportPOs = async (file: File, year: number, mode: 'update' | 'replace') => {
    setImportError(null)
    try {
      const result = await importPOs.mutateAsync({ file, year, mode })
      setImportOpen(false)
      setSelectedYear(year)
      refetchPOs()
      const unmatched = result.unmatchedSuppliers
      const summary =
        result.mode === 'replace'
          ? t('Replaced {year} with {n} POs.')
              .replace('{year}', String(year))
              .replace('{n}', String(result.imported))
          : t('{year}: {updated} updated, {created} added.')
              .replace('{year}', String(year))
              .replace('{updated}', String(result.updated))
              .replace('{created}', String(result.created))
      setImportNotice(
        summary +
          (unmatched.length
            ? ` ${t('{n} supplier(s) not in the whitelist:').replace('{n}', String(unmatched.length))} ${unmatched.join(', ')}`
            : '')
      )
    } catch (e) {
      setImportError(e instanceof Error ? e.message : t('Import failed'))
    }
  }

  const currentYear = new Date().getFullYear()

  // Add current year to list if not present
  const availableYears = useMemo(() => {
    const yearSet = new Set(years)
    yearSet.add(currentYear)
    customYears.forEach((year) => yearSet.add(year))
    return Array.from(yearSet).sort((a, b) => b - a)
  }, [years, currentYear, customYears])

  const openAddYear = (onAdded?: (year: number) => void) => {
    setAddYear({ open: true, onAdded, value: '', error: '' })
  }

  const confirmAddYear = () => {
    const parsed = Number(addYear.value)
    if (!Number.isInteger(parsed) || parsed < 2000 || parsed > 2100) {
      setAddYear((s) => ({ ...s, error: t('Please enter a valid year (2000–2100)') }))
      return
    }
    setCustomYears((prev) => (prev.includes(parsed) ? prev : [...prev, parsed]))
    setSelectedYear(parsed)
    addYear.onAdded?.(parsed)
    setAddYear({ open: false, value: '', error: '' })
  }

  const filteredPO = filteredPOId ? pos.find((po) => po.id === filteredPOId) : null

  return (
    <Page>
      <PageHeader
        title={t('PO Tracking')}
        actions={
          <>
            <Select<string>
              value={String(selectedYear)}
              ariaLabel={t('Year')}
              onChange={(value) => {
                const parsed = Number(value)
                if (parsed === ADD_YEAR_VALUE) {
                  openAddYear()
                  return
                }
                setSelectedYear(parsed)
              }}
              options={[
                ...availableYears.map((year) => ({
                  value: String(year),
                  label: `${t('Year')} ${year}`,
                })),
                { value: String(ADD_YEAR_VALUE), label: `+ ${t('Add new year')}` },
              ]}
            />
            <Button variant="ghost" onClick={() => setImportOpen(true)}>
              <Icon name="upload" size={15} />
              {t('Import POs')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => void handleApplyAssignments()}
              disabled={applyAssignments.isPending}
              title={t(
                'Fill empty entity, approver and legal counsel fields of existing invoices from their PO',
              )}
            >
              <Icon name="check" size={15} />
              {t('Fill assignments')}
            </Button>
            <Button variant="accent" onClick={handleCreatePO}>
              <Icon name="plus" size={15} strokeWidth={2.4} />
              {t('New PO')}
            </Button>
          </>
        }
      />

      {importNotice && <Notice onClose={() => setImportNotice(null)}>{importNotice}</Notice>}

      <Card>
        <CardTitle
          note={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {forecast && (
                <Pill
                  fg={confidenceTone(forecast.forecastMeta.confidence).fg}
                  bg={confidenceTone(forecast.forecastMeta.confidence).bg}
                  border={confidenceTone(forecast.forecastMeta.confidence).fg}
                >
                  {t('Forecast')} {t(forecast.forecastMeta.confidence)}
                </Pill>
              )}
              <Select<LineTypeOption>
                value={lineType}
                onChange={setLineType}
                ariaLabel={t('Line shape')}
                height={34}
                options={lineTypeOptions.map((o) => ({
                  value: o.value,
                  label: `${t('Line')}: ${t(o.label)}`,
                }))}
              />
            </div>
          }
        >
          {filteredPO
            ? `${t('Invoiced amount over time')} — PO ${filteredPO.poNumber}`
            : `${t('Invoiced amount over time')} — ${t('all POs')}`}
          {filteredPO && (
            <button
              type="button"
              className="fx-link-quiet"
              style={{ marginLeft: 10, fontSize: 12 }}
              onClick={() => setFilteredPOId(null)}
            >
              {t('Clear filter')}
            </button>
          )}
        </CardTitle>

        {forecastLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <CircularProgress />
          </div>
        ) : forecast ? (
          <OverviewChart data={forecast} lineType={lineType} />
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('No forecast data available for the selected period.')}
          </Alert>
        )}
      </Card>

      <POTable
        pos={pos}
        loading={posLoading}
        onEdit={handleEditPO}
        onDelete={handleDeletePO}
        onFilter={handleFilterByPO}
        filteredPOId={filteredPOId}
      />

      {/* Create/Edit Dialog */}
      <PODialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSavePO}
        po={editingPO}
        availableYears={availableYears}
        onAddYear={openAddYear}
      />

      {/* Delete confirmation */}
      <Dialog open={deleteTargetId !== null} onClose={() => setDeleteTargetId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('Delete PO?')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            {t(
              'The purchase order is deleted for good. Linked invoices only lose the assignment — they are not deleted.',
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setDeleteTargetId(null)}>{t('Cancel')}</MuiButton>
          <MuiButton onClick={confirmDeletePO} color="error" variant="contained">
            {t('Delete')}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Add year */}
      <Dialog open={addYear.open} onClose={() => setAddYear({ open: false, value: '', error: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>{t('Add year')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('Year')}
            type="number"
            value={addYear.value}
            onChange={(e) => setAddYear((s) => ({ ...s, value: e.target.value, error: '' }))}
            onKeyDown={(e) => { if (e.key === 'Enter') confirmAddYear() }}
            error={!!addYear.error}
            helperText={addYear.error || t('e.g. 2027')}
            fullWidth
            autoFocus
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setAddYear({ open: false, value: '', error: '' })}>
            {t('Cancel')}
          </MuiButton>
          <MuiButton onClick={confirmAddYear} variant="contained">
            {t('Add')}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Import PO list */}
      <POImportDialog
        open={importOpen}
        defaultYear={selectedYear}
        loading={importPOs.isPending}
        error={importError}
        onClose={() => {
          setImportOpen(false)
          setImportError(null)
        }}
        onImport={handleImportPOs}
      />
    </Page>
  )
}

interface PODialogProps {
  open: boolean
  onClose: () => void
  onSave: (data: PoFormData) => void
  po: PurchaseOrderWithUsage | null
  availableYears: number[]
  onAddYear: (onAdded?: (year: number) => void) => void
}

function PODialog({ open, onClose, onSave, po, availableYears, onAddYear }: PODialogProps) {
  const { t } = useT()
  const [poNumber, setPONumber] = useState('')
  const [pivotPoNumber, setPivotPoNumber] = useState('')
  const [supplierName, setSupplierName] = useState('')
  const [description, setDescription] = useState('')
  const [responsiblePerson, setResponsiblePerson] = useState('')
  const [legalCounsel, setLegalCounsel] = useState('')
  const [entity, setEntity] = useState('')
  const [valueTotalEur, setValueTotalEur] = useState('')
  const [year, setYear] = useState<number>(new Date().getFullYear())
  const [error, setError] = useState('')

  // Reset form when PO changes or dialog opens
  useEffect(() => {
    if (open) {
      setError('')
      if (po) {
        setPONumber(po.poNumber)
        setPivotPoNumber(po.pivotPoNumber || '')
        setSupplierName(po.supplierName || '')
        setDescription(po.description || '')
        setResponsiblePerson(po.responsiblePerson || '')
        setLegalCounsel(po.legalCounsel || '')
        setEntity(po.entity || '')
        setValueTotalEur(po.valueTotalEur.toString())
        setYear(po.year)
      } else {
        setPONumber('')
        setPivotPoNumber('')
        setSupplierName('')
        setDescription('')
        setResponsiblePerson('')
        setLegalCounsel('')
        setEntity('')
        setValueTotalEur('')
        setYear(new Date().getFullYear())
      }
    }
  }, [open, po])

  const handleSave = () => {
    const value = parseFloat(valueTotalEur)
    if (!poNumber.trim()) {
      setError(t('PO number is required'))
      return
    }
    if (isNaN(value) || value < 0) {
      setError(t('Please enter a valid amount'))
      return
    }
    setError('')

    onSave({
      poNumber: poNumber.trim(),
      pivotPoNumber: pivotPoNumber.trim() || null,
      supplierName: supplierName.trim() || null,
      description: description.trim() || null,
      responsiblePerson: responsiblePerson.trim() || null,
      legalCounsel: legalCounsel || null,
      entity: entity || null,
      valueTotalEur: value,
      year,
    })

    // Reset form
    setPONumber('')
    setPivotPoNumber('')
    setSupplierName('')
    setDescription('')
    setValueTotalEur('')
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{po ? t('Edit PO') : t('Create new PO')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('PO number')}
            value={poNumber}
            onChange={(e) => setPONumber(e.target.value)}
            fullWidth
            autoFocus
            placeholder={`${t('e.g.')} PO-2024-001`}
          />

          <TextField
            label={t('Pivot PO')}
            value={pivotPoNumber}
            onChange={(e) => setPivotPoNumber(e.target.value)}
            fullWidth
            placeholder={`${t('e.g.')} PV-2026-01`}
            helperText={t('Optional — the umbrella PO this one rolls up to')}
          />

          <TextField
            label={t('Supplier')}
            value={supplierName}
            onChange={(e) => setSupplierName(e.target.value)}
            fullWidth
            placeholder={`${t('e.g.')} Hartmann & Voss Rechtsanwälte`}
            helperText={t('Optional — matched against the supplier whitelist on save')}
          />

          <TextField
            label={t('Description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder={t('Optional description')}
          />

          <TextField
            label={t('Responsible person (approver)')}
            value={responsiblePerson}
            onChange={(e) => setResponsiblePerson(e.target.value)}
            fullWidth
            placeholder={`${t('e.g.')} Christina Stumpf`}
            helperText={t('Approves invoices on this PO — shown as initials in the Approver column')}
          />

          <FormControl fullWidth>
            <InputLabel shrink>{t('Legal counsel')}</InputLabel>
            <MuiSelect
              value={legalCounsel}
              displayEmpty
              notched
              onChange={(e) => setLegalCounsel(String(e.target.value))}
              label={t('Legal counsel')}
            >
              <MenuItem value="">{t('Same as responsible person')}</MenuItem>
              {LEGAL_COUNSELS.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel shrink>{t('Entity')}</InputLabel>
            <MuiSelect
              value={entity}
              displayEmpty
              notched
              onChange={(e) => setEntity(String(e.target.value))}
              label={t('Entity')}
            >
              <MenuItem value="">{t('From the PO number')}</MenuItem>
              {INVOICE_ENTITIES.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <TextField
            label={t('Total value (EUR)')}
            value={valueTotalEur}
            onChange={(e) => setValueTotalEur(e.target.value)}
            fullWidth
            type="number"
            InputProps={{
              startAdornment: <Box sx={{ mr: 1 }}>€</Box>,
            }}
          />

          <FormControl fullWidth>
            <InputLabel>{t('Year')}</InputLabel>
            <MuiSelect
              value={year}
              onChange={(e) => {
                const value = Number(e.target.value)
                if (value === ADD_YEAR_VALUE) {
                  onAddYear((newYear) => setYear(newYear))
                  return
                }
                setYear(value)
              }}
              label={t('Year')}
            >
              {availableYears.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
              <MenuItem value={ADD_YEAR_VALUE}>+ {t('Add new year')}</MenuItem>
            </MuiSelect>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>{t('Cancel')}</MuiButton>
        <MuiButton onClick={handleSave} variant="contained">
          {po ? t('Update') : t('Create')}
        </MuiButton>
      </DialogActions>
    </Dialog>
  )
}
